import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 6;
const POLL_INTERVAL = 2500;

const PaymentResult = () => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("checking"); // checking | success | failed | cancelled
  const [data, setData] = useState(null);

  useEffect(() => {
    const cleanUrl = () => {
      const u = new URL(window.location.href);
      u.searchParams.delete("payment");
      u.searchParams.delete("session_id");
      window.history.replaceState({}, "", u.toString());
    };

    const pollStatus = async (sessionId, attempt) => {
      if (attempt >= MAX_ATTEMPTS) {
        setMode("failed");
        cleanUrl();
        return;
      }
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        const { payment_status, status } = res.data;
        if (payment_status === "paid") {
          setData(res.data);
          setMode("success");
          cleanUrl();
          return;
        }
        if (status === "expired" || payment_status === "failed") {
          setMode("failed");
          cleanUrl();
          return;
        }
        setTimeout(() => pollStatus(sessionId, attempt + 1), POLL_INTERVAL);
      } catch (err) {
        console.error(err);
        setTimeout(() => pollStatus(sessionId, attempt + 1), POLL_INTERVAL);
      }
    };

    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const sessionId = params.get("session_id");

    if (payment === "cancelled") {
      setMode("cancelled");
      setOpen(true);
      cleanUrl();
      return;
    }

    if (payment === "success" && sessionId) {
      setOpen(true);
      setMode("checking");
      pollStatus(sessionId, 0);
    }
  }, []);

  if (!open) return null;

  return (
    <div
      data-testid="payment-result-overlay"
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
    >
      <div className="bg-white max-w-md w-full p-8 md:p-10 relative shadow-2xl">
        <button
          data-testid="payment-result-close"
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 text-[#4A4A4A] hover:text-[#121212]"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {mode === "checking" && (
          <div className="text-center py-4">
            <Loader2 className="mx-auto animate-spin text-[#C62828]" size={36} />
            <h3 className="mt-5 font-heading text-2xl text-[#121212]">
              Confirming your payment...
            </h3>
            <p className="mt-2 text-sm text-[#4A4A4A]">
              This usually takes a few seconds.
            </p>
          </div>
        )}

        {mode === "success" && (
          <div className="text-center py-2">
            <CheckCircle2 className="mx-auto text-[#2E5D4B]" size={44} />
            <h3 className="mt-5 font-heading text-3xl text-[#121212]">
              Reservation confirmed
            </h3>
            <p className="mt-3 text-[#4A4A4A] leading-relaxed">
              Thank you! Your{" "}
              <strong>${data?.amount?.toFixed(2)} {data?.currency?.toUpperCase()}</strong>{" "}
              deposit has been received. Mei or one of our human curators
              will reach out within 24 hours to finalize your itinerary.
            </p>
            <button
              data-testid="payment-success-close"
              onClick={() => setOpen(false)}
              className="mt-7 bg-[#C62828] hover:bg-[#B71C1C] text-white px-7 py-3.5 uppercase tracking-wider text-sm font-semibold transition-colors"
            >
              Continue exploring
            </button>
          </div>
        )}

        {mode === "failed" && (
          <div className="text-center py-2">
            <XCircle className="mx-auto text-[#C62828]" size={44} />
            <h3 className="mt-5 font-heading text-2xl text-[#121212]">
              Payment did not complete
            </h3>
            <p className="mt-3 text-[#4A4A4A]">
              Something went wrong while confirming your payment. Please try
              again or contact us on WhatsApp.
            </p>
            <button
              data-testid="payment-failed-close"
              onClick={() => setOpen(false)}
              className="mt-6 border border-black/20 hover:border-[#C62828] px-7 py-3 uppercase tracking-wider text-sm"
            >
              Close
            </button>
          </div>
        )}

        {mode === "cancelled" && (
          <div className="text-center py-2">
            <XCircle className="mx-auto text-[#4A4A4A]" size={40} />
            <h3 className="mt-5 font-heading text-2xl text-[#121212]">
              Payment cancelled
            </h3>
            <p className="mt-3 text-[#4A4A4A]">
              No charge was made. You can reserve anytime.
            </p>
            <button
              data-testid="payment-cancelled-close"
              onClick={() => setOpen(false)}
              className="mt-6 bg-[#C62828] hover:bg-[#B71C1C] text-white px-7 py-3 uppercase tracking-wider text-sm transition-colors"
            >
              OK
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentResult;
